import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
    CostTrackingContextType,
    ExpenseRecord,
    ExpenseCategory,
    UnitExpenseBudget,
    CategoryCostBreakdown,
} from "../types/costTrackingTypes";
import { getCostBreakdownForUnit as getCostBreakdownUtil } from "../utils/costTrackingUtils";
import {
    fetchExpenseRecordsByCompany,
    fetchExpenseCategoriesByCompany,
    fetchUnitExpenseBudgetsByCompany,
} from "../graphql/costTrackingQueries";
import { useUserContext } from "./UserContext";
import { iProvider } from "../types";

const defaultCostTrackingContext: CostTrackingContextType = {
    expenseRecords: [],
    expenseCategories: [],
    unitExpenseBudgets: [],
    isLoading: false,
    refreshExpenseData: async () => { },
    getExpensesForUnit: () => [],
    getTotalCostForUnit: () => 0,
    getCostBreakdownForUnit: () => [],
    getStartingBalanceForUnitCategory: () => null,
};

export const CostTrackingContext = createContext<CostTrackingContextType>(defaultCostTrackingContext);

// eslint-disable-next-line react-refresh/only-export-components
export const useCostTracking = () => useContext(CostTrackingContext);

export const CostTrackingProvider = ({ children }: iProvider) => {
    const { companyUser, companyOwner } = useUserContext();

    const [expenseRecords, setExpenseRecords] = useState<ExpenseRecord[]>([]);
    const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
    const [unitExpenseBudgets, setUnitExpenseBudgets] = useState<UnitExpenseBudget[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId;

    const refreshExpenseData = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        try {
            const [records, categories, budgets] = await Promise.all([
                fetchExpenseRecordsByCompany(companyId),
                fetchExpenseCategoriesByCompany(companyId),
                fetchUnitExpenseBudgetsByCompany(companyId),
            ]);

            setExpenseRecords((records ?? []) as ExpenseRecord[]);
            setExpenseCategories((categories ?? []) as ExpenseCategory[]);
            setUnitExpenseBudgets((budgets ?? []) as UnitExpenseBudget[]);
        } catch (error) {
            console.error("Failed to fetch cost tracking data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (companyId) {
            refreshExpenseData();
        } else {
            setExpenseRecords([]);
            setExpenseCategories([]);
            setUnitExpenseBudgets([]);
        }
    }, [companyId, refreshExpenseData]);

    const getExpensesForUnit = useCallback(
        (unitId: string): ExpenseRecord[] => {
            return expenseRecords.filter((r) => r.unitId === unitId);
        },
        [expenseRecords],
    );

    const getTotalCostForUnit = useCallback(
        (unitId: string): number => {
            return expenseRecords
                .filter((r) => r.unitId === unitId)
                .reduce((sum, r) => sum + r.totalExpense, 0);
        },
        [expenseRecords],
    );

    const getCostBreakdownForUnit = useCallback(
        (unitId: string): CategoryCostBreakdown[] => {
            const unitExpenses = expenseRecords.filter((r) => r.unitId === unitId);
            return getCostBreakdownUtil(unitExpenses);
        },
        [expenseRecords],
    );

    const getStartingBalanceForUnitCategory = useCallback(
        (unitId: string, categoryId: string): number | null => {
            const unitBudget = unitExpenseBudgets.find(
                (b) => b.unitId === unitId && b.expenseCategoryId === categoryId,
            );
            if (unitBudget) return unitBudget.startingBalance;

            const category = expenseCategories.find((c) => c.id === categoryId);
            if (category?.startingBalance != null) return category.startingBalance;

            return null;
        },
        [unitExpenseBudgets, expenseCategories],
    );

    const value: CostTrackingContextType = {
        expenseRecords,
        expenseCategories,
        unitExpenseBudgets,
        isLoading,
        refreshExpenseData,
        getExpensesForUnit,
        getTotalCostForUnit,
        getCostBreakdownForUnit,
        getStartingBalanceForUnitCategory,
    };

    return <CostTrackingContext.Provider value={value}>{children}</CostTrackingContext.Provider>;
};
